import { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, Loader2, RefreshCw, Route } from 'lucide-react';
import { Link } from 'react-router-dom';

import ShopSelect from '../../../components/ShopSelect';
import { createClientRequestId } from '../../../utils/clientRequestId';
import { businessDeliveryOperationsService as deliveryApi } from '../../services/businessDeliveryOperationsService';

const DISPOSITIONS = Object.freeze([
  ['SAME_RUN_RETRY', 'Retry on this trip'],
  ['RESCHEDULE_OUTSIDE_RUN', 'Return and reschedule'],
  ['RETURN_REQUIRED', 'Return to the business'],
  ['MANAGEMENT_REVIEW', 'Send to management review'],
]);

const RESULT_COPY = Object.freeze({
  SAME_RUN_RETRY: 'Retry authorized. The Rider now has the second attempt on this trip.',
  RESCHEDULE_OUTSIDE_RUN: 'Reschedule recorded. The Parcel stays with the Rider until the return is confirmed.',
  RETURN_REQUIRED: 'Return required. The Rider can now present this Parcel at the returns counter.',
  MANAGEMENT_REVIEW: 'Management review recorded. The Parcel remains accounted to the Rider until it is resolved.',
});

const FAILURE_LABELS = Object.freeze({
  RECIPIENT_UNAVAILABLE: 'Recipient unavailable',
  CUSTOMER_UNREACHABLE: 'Customer unreachable',
  CUSTOMER_REQUESTED_RESCHEDULE: 'Customer requested another time',
  COD_NOT_PROVIDED: 'Cash was not provided',
  ADDRESS_NOT_FOUND: 'Address could not be found',
  UNSAFE_OR_INACCESSIBLE: 'Location was unsafe or inaccessible',
  PARCEL_DAMAGED: 'Parcel was damaged',
  OTHER: 'Another delivery problem',
});

function friendlyError(error) {
  const code = error?.response?.data?.code;
  const status = error?.response?.status;
  if (status === 403) return 'You no longer have permission to decide failed stops.';
  if (status === 409 || [
    'STOP_DISPOSITION_VERSION_CONFLICT',
    'STOP_DISPOSITION_NOT_PENDING',
    'STOP_ALREADY_DISPOSED',
    'STOP_DISPOSITION_SUPERSEDED',
  ].includes(code)) {
    return 'This delivery changed while you were deciding. The latest queue is shown; review it before trying again.';
  }
  if (code === 'DELIVERY_RETRY_NOT_ELIGIBLE') {
    return 'This failure cannot be retried on the same trip. Choose a return, reschedule, or management review.';
  }
  if (code === 'DELIVERY_SAME_RUN_RETRY_LIMIT') {
    return 'This stop has already used its one same-trip retry. Choose another outcome.';
  }
  return error?.response?.data?.message
    || 'The decision could not be recorded. Check your connection and try again.';
}

function when(value) {
  if (!value) return 'Time unavailable';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Time unavailable';
  return new Intl.DateTimeFormat('en-PH', {
    dateStyle: 'medium', timeStyle: 'short',
  }).format(parsed);
}

export default function FailedStopDispositionQueue({ businessId, refreshRevision = 0, onChanged }) {
  const [items, setItems] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [busyAttemptId, setBusyAttemptId] = useState(null);
  const [error, setError] = useState('');
  const [loadFailed, setLoadFailed] = useState(false);
  const [notice, setNotice] = useState('');
  const requestKeys = useRef(new Map());

  const load = useCallback(async ({ quiet = false, preserveError = false, signal } = {}) => {
    if (!businessId) return;
    if (!quiet) setLoading(true);
    try {
      const result = await deliveryApi.stopDispositions(businessId, { signal });
      if (signal?.aborted) return;
      setItems(Array.isArray(result?.items) ? result.items : []);
      setLoadFailed(false);
      if (!preserveError) setError('');
    } catch (requestError) {
      if (signal?.aborted || requestError?.code === 'ERR_CANCELED') return;
      setError(friendlyError(requestError));
      setLoadFailed(true);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    const controller = new AbortController();
    load({ signal: controller.signal });
    return () => controller.abort();
  }, [load, refreshRevision]);

  const updateDraft = (attemptId, change) => setDrafts((current) => ({
    ...current,
    [attemptId]: { dispositionCode: '', justification: '', ...current[attemptId], ...change },
  }));

  const decide = async (item) => {
    const draft = drafts[item.attemptId] || {};
    const justification = String(draft.justification || '').replace(/\s+/g, ' ').trim();
    if (!draft.dispositionCode || justification.length < 10) return;
    const identity = `stop_disposition_${item.attemptId}_${draft.dispositionCode}`;
    if (!requestKeys.current.has(identity)) {
      requestKeys.current.set(identity, createClientRequestId(identity));
    }
    setBusyAttemptId(item.attemptId);
    setError('');
    setLoadFailed(false);
    setNotice('');
    try {
      const result = await deliveryApi.decideStopDisposition(businessId, item.attemptId, {
        dispositionCode: draft.dispositionCode,
        justification,
        expectedAttemptVersion: item.attemptVersion,
        expectedStopVersion: item.stopVersion,
        expectedShipmentVersion: item.shipmentVersion,
        expectedAssignmentVersion: item.assignmentVersion,
        expectedRunVersion: item.runVersion,
        idempotencyKey: requestKeys.current.get(identity),
      });
      requestKeys.current.delete(identity);
      setDrafts((current) => {
        const next = { ...current };
        delete next[item.attemptId];
        return next;
      });
      setNotice(RESULT_COPY[draft.dispositionCode]);
      await load({ quiet: true });
      await onChanged?.(result, item);
    } catch (requestError) {
      setError(friendlyError(requestError));
      if (requestError?.response?.status === 409) await load({ quiet: true, preserveError: true });
    } finally {
      setBusyAttemptId(null);
    }
  };

  // With nothing to decide, the queue is one line rather than a panel that
  // pushes real work down the page.
  if (!loading && !error && !notice && items.length === 0) {
    return (
      <section aria-labelledby="failed-stop-heading" className="flex flex-wrap items-center justify-between gap-3 rounded-panel border border-default bg-surface px-4 py-2">
        <h2 id="failed-stop-heading" className="flex items-center gap-2 text-sm font-normal">
          <CheckCircle2 className="h-4 w-4 shrink-0 text-success" aria-hidden="true" />
          No failed stops need a decision.
        </h2>
      </section>
    );
  }

  return (
    <section aria-labelledby="failed-stop-heading" className="rounded-panel border border-default bg-surface p-4 sm:p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 id="failed-stop-heading" className="text-lg font-semibold">Failed stops</h2>
          <p className="mt-1 max-w-2xl text-sm text-neutral-600 dark:text-neutral-300">
            Choose what happens next after a Rider records a delivery problem. The Parcel stays with the Rider until a physical return is confirmed.
          </p>
        </div>
      </div>

      {notice && <p role="status" aria-live="polite" className="mt-4 rounded-2xl border border-success bg-success-subtle p-4 text-sm text-success">{notice}</p>}
      {error && <div role="alert" className="mt-4 rounded-2xl border border-danger bg-danger-subtle p-4 text-sm text-danger"><p className="flex gap-2"><AlertTriangle className="h-5 w-5 shrink-0" aria-hidden="true" />{error}</p>{loadFailed ? <button type="button" onClick={() => load()} disabled={loading} className="mt-3 inline-flex min-h-10 items-center gap-2 rounded-control border border-danger px-3 font-semibold hover:bg-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus disabled:opacity-50"><RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin motion-reduce:animate-none' : ''}`} aria-hidden="true" />Try again</button> : null}</div>}

      {loading ? (
        <div role="status" className="grid min-h-40 place-items-center"><Loader2 className="h-7 w-7 animate-spin text-accent" aria-hidden="true" /><span className="sr-only">Loading failed stops</span></div>
      ) : items.length === 0 ? (
        <div className="mt-5 rounded-2xl border border-dashed border-neutral-300 p-8 text-center dark:border-neutral-700">
          <Route className="mx-auto h-7 w-7 text-neutral-400" aria-hidden="true" />
          <h3 className="mt-3 font-semibold">No failed stops need a decision</h3>
          <p className="mt-1 text-sm text-neutral-500">New delivery problems will appear here automatically.</p>
        </div>
      ) : (
        <div className="mt-5 grid gap-4 xl:grid-cols-2">
          {items.map((item) => {
            const draft = drafts[item.attemptId] || { dispositionCode: '', justification: '' };
            const normalizedJustification = String(draft.justification || '').replace(/\s+/g, ' ').trim();
            const busy = busyAttemptId === item.attemptId;
            return (
              <article key={item.attemptId} className="rounded-2xl border border-warning bg-warning-subtle p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h3 className="font-semibold">{item.orderReference || `Order ${item.orderId}`}</h3>
                    <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-300">Trip #{item.runId} · Stop {item.stopSequence}</p>
                    <div className="mt-2 flex flex-wrap gap-3 text-sm font-semibold"><Link to={`/business/delivery?view=parcels&parcel=${item.shipmentId}`} className="underline underline-offset-4">View parcel</Link><Link to={`/business/delivery?view=trips&trip=${item.runId}`} className="underline underline-offset-4">View trip</Link></div>
                  </div>
                  <span className="rounded-full bg-warning-subtle px-3 py-1 text-xs font-semibold text-warning">Awaiting decision</span>
                </div>
                <dl className="mt-4 grid gap-2 text-sm sm:grid-cols-2">
                  <div><dt className="text-neutral-500">Failure reason</dt><dd className="font-medium">{FAILURE_LABELS[item.reason] || String(item.reason || 'Not recorded').replaceAll('_', ' ').toLowerCase()}</dd></div>
                  <div><dt className="text-neutral-500">Reported</dt><dd className="font-medium">{when(item.failedAt)}</dd></div>
                </dl>
                <div className="mt-4 grid gap-1 text-sm font-medium">
                  <span>What should happen next?</span>
                  <ShopSelect
                    value={draft.dispositionCode}
                    onValueChange={(value) => updateDraft(item.attemptId, { dispositionCode: value })}
                    options={[
                      { value: '', label: 'Choose an outcome' },
                      ...DISPOSITIONS.map(([value, label]) => ({ value, label })),
                    ]}
                    disabled={busyAttemptId != null}
                    ariaLabel="What should happen next?"
                    align="start"
                    minPanelWidth={240}
                    triggerClassName="min-h-11 w-full rounded-control px-3 text-sm font-normal"
                    panelClassName="py-1"
                  />
                </div>
                <label className="mt-3 grid gap-1 text-sm font-medium" htmlFor={`justification-${item.attemptId}`}>
                  Why is this the right decision?
                  <textarea id={`justification-${item.attemptId}`} value={draft.justification} onChange={(event) => updateDraft(item.attemptId, { justification: event.target.value })} disabled={busyAttemptId != null} minLength={10} maxLength={1000} rows={3} aria-describedby={`justification-help-${item.attemptId}`} className="rounded-xl border border-neutral-300 bg-white p-3 dark:border-neutral-600 dark:bg-neutral-950" />
                </label>
                <p id={`justification-help-${item.attemptId}`} className="mt-1 text-xs text-neutral-500">Required, 10–1000 characters. This explanation is stored with the decision.</p>
                <button type="button" onClick={() => decide(item)} disabled={busyAttemptId != null || !draft.dispositionCode || normalizedJustification.length < 10 || normalizedJustification.length > 1000} className="mt-4 inline-flex min-h-11 items-center gap-2 rounded-xl bg-accent px-4 text-sm font-semibold text-on-accent hover:bg-accent-hover active:bg-accent-active focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus disabled:opacity-50">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Route className="h-4 w-4" aria-hidden="true" />}
                  Record decision
                </button>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}
